import {useEffect, useCallback, useRef} from '@wordpress/element';
import {useApiRequests} from '../../../hooks';

export const usePaymentCallbacks = (
    {
        paypal,
        shipping,
        billing,
        setPaymentData,
    }) => {

    const orderIdRef = useRef(null);
    const currentData = useRef({billing, shipping, paypal});

    const {
        createOrder,
        onShippingChange
    } = useApiRequests({
        isExpress: true,
        paymentMethod: 'ppcp_googlepay'
    })

    useEffect(() => {
        currentData.current = {billing, shipping, paypal};
    }, [
        billing,
        shipping,
        paypal
    ]);

    /**
     * Handle payment authorization callback from Google Pay
     * This is called when the user authorizes payment in the Google Pay sheet
     */
    const onPaymentAuthorized = useCallback(async (paymentData) => {
        try {

            if (!orderIdRef.current) {
                orderIdRef.current = await createOrder(currentData.current);
                setPaymentData({orderId: orderIdRef.current}, false);
            }

            const {paypal} = currentData.current;

            // Confirm the order with PayPal
            const response = await paypal.Googlepay().confirmOrder({
                orderId: orderIdRef.current,
                paymentMethodData: paymentData.paymentMethodData
            });

            if (response.status === 'APPROVED') {
                orderIdRef.current = null;
                return {
                    transactionState: 'SUCCESS'
                };
            } else {
                return {
                    transactionState: 'ERROR',
                    error: {
                        intent: 'PAYMENT_AUTHORIZATION',
                        message: 'Payment authorization failed'
                    }
                };
            }
        } catch (error) {
            console.error('Payment authorization error:', error);
            return {
                transactionState: 'ERROR',
                error: {
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: error?.message || 'Payment authorization failed'
                }
            };
        }
    }, [
        createOrder,
        setPaymentData
    ]);

    /**
     * Handle payment data changes (shipping address/option changes)
     * This is called when the user changes shipping address or shipping option in the Google Pay sheet
     */
    const onPaymentDataChanged = useCallback(async (data) => {
        try {
            const shippingAddress = data.shippingAddress;
            const shippingOption = data.shippingOptionData?.id;

            // Format shipping address for WooCommerce
            const address = {
                city: shippingAddress?.locality,
                postcode: shippingAddress?.postalCode,
                state: shippingAddress?.administrativeArea,
                country: shippingAddress?.countryCode
            };

            const props = {address, shipping_method: shippingOption}

            const response = await onShippingChange(props, currentData.current);

            return {
                newTransactionInfo: {
                    totalPrice: response.total,
                    totalPriceStatus: 'FINAL',
                    countryCode: response.country_code,
                    currencyCode: response.currency,
                    displayItems: response.display_items || []
                },
                ...(response.shipping_options && {
                    newShippingOptionParameters: {
                        shippingOptions: response.shipping_options,
                        ...(response.shipping_method && {
                            defaultSelectedOptionId: response.shipping_method
                        })
                    }
                })
            };
        } catch (error) {
            console.error('Payment data changed error:', error);

            // Handle errors based on the error data structure
            if (error?.data?.error) {
                return {
                    error: {
                        reason: error.data.error.reason || 'SHIPPING_ADDRESS_INVALID',
                        message: error.data.error.message || error.message,
                        intent: error.data.error.intent || 'SHIPPING_ADDRESS'
                    }
                };
            }

            return {
                error: {
                    reason: 'OTHER_ERROR',
                    message: error?.message || 'Unable to update payment information',
                    intent: 'SHIPPING_ADDRESS'
                }
            };
        }
    }, [
        onShippingChange
    ]);

    return {
        onPaymentAuthorized,
        onPaymentDataChanged
    };
};

export default usePaymentCallbacks;